import { AddressElement } from "@stripe/react-stripe-js";
import { Appearance } from "@stripe/stripe-js";
import _ from "lodash";
import moment from "moment";
import React, { Component } from "react";
import { Trans, withTranslation, WithTranslation } from "react-i18next";
import type Stripe from "stripe";
import styled, { css } from "styled-components";
import { v4 as uuid } from "uuid";

import { workspaces as workspacesApi } from "apis/callables";
import { ConflictError, ForbiddenError, NotFoundError } from "apis/errors";
import { IEnterpriseInvite, IWorkspace, IWorkspaceUserExtended, IWorkspaceUserGroup, IWorkspaceUserInvite, LegacyTeamMemberRole, WorkspacePlanTier } from "common/interfaces";
import stripeProducts from "common/stripeProducts";
import { Button } from "js/Components/Button";
import { Icon } from "js/Components/Icon";
import { upgradeWorkspacePlan } from "js/controllers/BillingController";
import { createWorkspace, deleteWorkspace, getWorkspaceUserGroups, getWorkspaceUserInvites, getWorkspaceUsers, inviteWorkspaceUsers, setPersistentWorkspaceId } from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { delimiterRegex, emailRegex } from "common/regex";
import { auth } from "js/firebase/auth";
import AddTeamMembers from "js/react/components/AddTeamMembers";
import { ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import ErrorDialog from "js/react/components/Dialogs/ErrorDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { browserHistory } from "js/react/history";
import SuccessfulPurchaseDialog from "js/react/views/Billing/dialogs/SuccessfulPurchaseDialog";
import { TaxIdForm } from "js/react/views/Billing/Checkout/components/TaxIdForm/TaxIdForm";
import { guessUserCountry } from "js/react/views/Billing/Checkout/utils/countries";
import withElements from "js/react/views/Billing/withElements";
import withStripe from "js/react/views/Billing/withStripe";
import { getWorkspaceUserRoleFromLegacyRole } from "common/utils/workspaces/users";

import {
    Container,
    ContentContainer,
    Heading,
    IconBox,
    PaymentDetailsContainer,
    ScrollContainer,
    WorkspaceSettingsContainer
} from "../../components/FormStyling";

const logger = getLogger(LogGroup.INVITES);

const paragraphCSS = css`
    font-size: 15px;
    color: #777;
    user-select: none;
    margin: 3px 0;

    >b {
        color: black;
    }

    >a {
        text-decoration: none;
        color: #11a9e2;
    }
`;

const InviteText = styled.div`
    margin: 10px 0;
    >p {
        ${paragraphCSS}
    }
`;

const ButtonsContainer = styled.div`
    width: 100%;
    display: flex;
    flex-flow: row;
    justify-content: flex-end;
    align-items: center;
    margin-top: 10px;
    gap: 15px;

    >button {
        padding: 6px 30px;
    }
`;

const InviteInfoContainer = styled.div`
    margin: 30px 0;

    >p {
        ${paragraphCSS}
    }
`;

const appearance: Appearance = {
    labels: "floating",
    variables: {
        spacingGridRow: "12px",
        spacingGridColumn: "12px",
        spacingUnit: "4px",
        spacingTab: "4px",
        colorDanger: "#b40000",
        colorPrimary: "#11a9e2",
        colorPrimaryText: "#11a9e2",
        colorText: "#222",
        colorTextSecondary: "#666",
        colorTextPlaceholder: "#999",
        fontFamily: "Source Sans Pro",
        fontWeightNormal: "400",
        fontWeightBold: "600",
        fontSizeBase: "16px",
        fontSizeSm: "14px",
        borderRadius: "0"
    },
    rules: {
        ".Input": {
            fontSize: "var(--fontSizeBase)",
            lineHeight: "1.15",
            letterSpacing: "-0.01em",
            padding: "3px 0 7px",
            boxShadow: "none",
            border: `none`,
            color: "var(--colorTextSecondary)",
            borderBottom: "1px solid #d0d0d0",
            marginTop: "8px"
        },
        ".Input:focus": {
            boxShadow: "none",
            outline: "none",
            borderBottom: "1px solid var(--colorPrimary)",
        },
        ".Input--invalid": {
            borderBottom: "1px solid var(--colorDanger)",
            color: "var(--colorText)",
            boxShadow: "none",
        },
        ".Input:hover": {
            boxShadow: "none",
            borderBottom: "1px solid #333",
        },
        ".Label": {
            fontSize: "var(--fontSizeBase)",
            lineHeight: "18px",
            letterSpacing: "-0.01em",
            color: "var(--colorTextSecondary)",
            margin: "4px 0 7px"
        },
        ".Label--floating": {
            fontSize: "var(--fontSizeSm)",
            lineHeight: "16px",
            opacity: "1",
            margin: "-2px 0 0",
            padding: "0",
            transition: "all 300ms cubic-bezier(0.5, 0, 0.2, 1)",
            color: "var(--colorTextSecondary)",
        },
        ".Error": {
            fontSize: "12px",
            lineHeight: "15px",
            color: "var(--colorDanger)"
        },
        ".TermsText": {
            fontSize: "14px",
            lineHeight: "1.6",
            letterSpacing: "-0.02em",
            color: "var(--colorTextSecondary)",
            marginTop: "0px"
        }
    }
};

class UpgradeFailedError extends Error {
    constructor(originalError: Error) {
        super(originalError.message);
        this.name = "UpgradeFailedError";
        this.stack = originalError.stack;
    }
}

interface TeamMember {
    id: string;
    email: string;
    role: LegacyTeamMemberRole;
    isDisabled?: boolean;
}

interface EnterpriseCheckoutProps extends WithTranslation {
    enterpriseInviteId: string;
    hideBackdrop?: boolean;
    closeDialog?: () => void;
}

interface EnterpriseCheckoutState {
    status: "loading" | "ready" | "applyingInvite" | "success" | "wrongUser" | "inviteNotFound" | "serverError" | "alreadyAccepted";
    teamName: string;
    invite: IEnterpriseInvite & { invitedByEmail?: string; invitedByDisplayName?: string; } | null;
    address: Stripe.Address;
    isAddressValid: boolean;
    members: TeamMember[];
    taxId: string;
    taxIdType: Stripe.TaxId.Type | null;
    isTaxIdValid: boolean;
    taxAmount: number;
    workspace: IWorkspace | null;
    workspaceUsers: IWorkspaceUserExtended[];
    workspaceUserInvites: IWorkspaceUserInvite[];
    workspaceUserGroups: IWorkspaceUserGroup[];
}

class EnterpriseCheckout extends Component<EnterpriseCheckoutProps, EnterpriseCheckoutState> {
    private guessedUserCountry: string;

    constructor(props: EnterpriseCheckoutProps) {
        super(props);

        const firebaseUser = auth().currentUser;

        this.state = {
            status: "loading",
            teamName: "",
            invite: null,
            address: {
                line1: "",
                line2: "",
                postal_code: "",
                country: "",
                city: "",
                state: ""
            },
            isAddressValid: false,
            members: [{
                id: firebaseUser.uid,
                email: firebaseUser.email || "",
                role: LegacyTeamMemberRole.OWNER,
                isDisabled: true
            }],
            taxId: "",
            taxIdType: null,
            isTaxIdValid: true,
            taxAmount: 0,
            workspace: null,
            workspaceUsers: [],
            workspaceUserInvites: [],
            workspaceUserGroups: []
        };

        this.guessedUserCountry = guessUserCountry();
    }

    get validMembers() {
        return this.state.members.filter(member => member.email && emailRegex.test(member.email));
    }

    get workspaceUsersToInvite() {
        const { workspaceUsers, workspaceUserInvites, workspaceUserGroups } = this.state;

        const currentUser = auth().currentUser;

        return this.validMembers
            .filter(m => m.email !== currentUser?.email)
            .filter(m => !workspaceUsers.some(u => u.email === m.email))
            .filter(m => !workspaceUserInvites.some(i => i.email === m.email))
            .map(m => ({
                email: m.email,
                role: getWorkspaceUserRoleFromLegacyRole(m.role)
            }));
    }

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(prevProps: EnterpriseCheckoutProps, prevState: EnterpriseCheckoutState) {
        const { isAddressValid, address, isTaxIdValid, taxId, taxIdType } = this.state;

        if (isAddressValid !== prevState.isAddressValid || isTaxIdValid !== prevState.isTaxIdValid) {
            this.calculateTaxAmount();
        } else if (isAddressValid && (address.country !== prevState.address.country || address.postal_code !== prevState.address.postal_code)) {
            this.calculateTaxAmount();
        } else if (isTaxIdValid && (taxId !== prevState.taxId || taxIdType !== prevState.taxIdType)) {
            this.calculateTaxAmount();
        }
    }

    async load() {
        const { enterpriseInviteId } = this.props;

        try {
            const invite = await workspacesApi.getEnterpriseInvite({ inviteId: enterpriseInviteId });

            this.setState({
                invite,
                status: "ready",
                teamName: invite.teamName,
                members: [
                    this.state.members[0],
                    ...[...new Array(Math.min(5, invite.seatCount - 1)).keys()].map(idx => ({
                        id: uuid(),
                        email: "",
                        role: idx === 0 ? LegacyTeamMemberRole.LIBRARIAN : LegacyTeamMemberRole.MEMBER_LICENSED,
                    }))
                ]
            });
        } catch (err) {
            logger.error(err, "[EnterpriseCheckout] workspacesApi.getEnterpriseInvite() failed", { enterpriseInviteId });

            if (err instanceof ForbiddenError) {
                this.setState({ status: "wrongUser" });
                return;
            }

            if (err instanceof NotFoundError) {
                this.setState({ status: "inviteNotFound" });
                return;
            }

            if (err instanceof ConflictError) {
                this.setState({ status: "alreadyAccepted" });
                return;
            }

            this.setState({ status: "serverError" });
        }
    }

    fetchAndSetWorkspaceData = async (workspace: IWorkspace) => {
        const [workspaceUsers, workspaceUserInvites, workspaceUserGroups] = await Promise.all([
            getWorkspaceUsers(workspace.id),
            getWorkspaceUserInvites(workspace.id),
            getWorkspaceUserGroups(workspace.id)
        ]);

        return new Promise<void>(resolve => this.setState({
            workspace,
            workspaceUsers,
            workspaceUserInvites,
            workspaceUserGroups,
            teamName: workspace.name
        }, () => resolve()));
    }

    calculateTaxAmount = async () => {
        const {
            invite,
            address: {
                country,
                postal_code
            },
            taxId,
            isAddressValid
        } = this.state;

        if (!invite || !isAddressValid) {
            this.setState({ taxAmount: 0 });
            return;
        }

        try {
            const { taxAmount } = await workspacesApi.calculateTaxAmount({
                amount: invite.seatCount * invite.pricePerSeat,
                stripeProductId: stripeProducts[WorkspacePlanTier.ENTERPRISE].productId,
                countryCode: country,
                postalCode: postal_code,
                taxId
            });
            this.setState({ taxAmount });
        } catch (err) {
            logger.error(err, "[EnterpriseCheckout] failed to calculate tax");
            this.setState({ taxAmount: 0 });
        }
    }

    handleMemberUpdate = (id: string, propKey: keyof TeamMember, propValue: any) => {
        this.setState(prevState => ({
            ...prevState,
            members: _.cloneDeep(prevState.members).map(member => member.id === id ? { ...member, [propKey]: propValue } : { ...member })
        }));
    };

    handleAddMember = () => {
        this.setState(prevState => {
            return {
                ...prevState,
                members: _.cloneDeep(prevState.members).concat({
                    id: uuid(),
                    email: "",
                    role: LegacyTeamMemberRole.MEMBER_LICENSED
                })
            };
        });
    };

    handleRemoveMember = (id: string) => {
        this.setState(prevState => ({
            ...prevState,
            members: _.cloneDeep(prevState.members).filter(member => member.id !== id)
        }));
    };

    handleAcceptInvite = async () => {
        const { t } = this.props;
        const { address, teamName, invite, taxId, taxIdType } = this.state;

        if (!invite) {
            return;
        }

        this.setState({ status: "applyingInvite" });

        const progressDialog = ShowDialog(ProgressDialog, {
            title: t("Creating your workspace...")
        });

        try {
            const workspace = await createWorkspace({
                name: teamName,
                analyticsProps: {
                    plan_display_name: stripeProducts[WorkspacePlanTier.ENTERPRISE].productName
                }
            });

            await this.fetchAndSetWorkspaceData(workspace);

            progressDialog.setTitle(t("Workspace created! Applying invite..."));

            try {
                await upgradeWorkspacePlan({
                    workspaceId: workspace.id,
                    tier: WorkspacePlanTier.ENTERPRISE,
                    billingDetails: {
                        address: address,
                        taxId: taxId ? {
                            type: taxIdType,
                            value: taxId
                        } : undefined
                    },
                    analyticsProps: {
                        seat_recipients: this.validMembers.map(m => m.email)
                    },
                    enterpriseInviteId: invite.id
                });
            } catch (err) {
                // *For now*, we want to keep the old experience for users and delete the workspace when
                // the payment fails.
                // I don't want to hack the backend logic to support automatic workspace deletion as it'll
                // add a bunch of spaghetti code there and make the methods more complex, so I'm keeping
                // the backend logic generic and handling the removal explicitly here, it's safe to do it
                // from the client because if something goes wrong, we can handle workspaces without
                // subscriptions just fine.
                await deleteWorkspace(workspace.id);

                throw new UpgradeFailedError(err);
            }

            if (this.workspaceUsersToInvite.length > 0) {
                progressDialog.setTitle(t("Invite applied! Inviting your team members..."));
                await inviteWorkspaceUsers(workspace.id, this.workspaceUsersToInvite);
            }

            setPersistentWorkspaceId(workspace.id, "CREATED");

            this.setState({ status: "success" });
        } catch (err) {
            logger.error(err, "[EnterpriseCheckout] handleSubmit() failed");

            this.setState({ status: "serverError" });
        } finally {
            progressDialog.close();
        }
    }

    handleImportMembersFromFile = (file: File) => {
        const { t } = this.props;
        const { invite } = this.state;

        if (!invite) {
            return;
        }

        new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                resolve(reader.result as string);
            };
            reader.onerror = err => {
                reject(err);
            };
            reader.readAsBinaryString(file);
        })
            .then(fileContent => {
                const emails = fileContent
                    .split(delimiterRegex)
                    .map(record => record.trim().toLowerCase())
                    .filter(record => emailRegex.test(record))
                    .slice(0, invite.seatCount - 1);

                if (emails.length > 0) {
                    this.setState(state => ({
                        ...state,
                        members: [
                            state.members[0],
                            ...emails.map(email => ({
                                id: uuid(),
                                email,
                                role: LegacyTeamMemberRole.MEMBER_LICENSED
                            }))
                        ]
                    }));
                }
            })
            .catch(err => {
                logger.error(err, "[EnterpriseCheckout] handleImportMembersFromFile() failed", { enterpriseInviteId: invite.id });

                ShowErrorDialog({
                    title: t("Sorry, we couldn't read your file, try a different one")
                });
            });
    }

    handleAddressChange = ({ complete, value: { address, name } }: { complete: boolean; value: { address: Stripe.Address; name: string } }) => {
        this.setState({ address: { ...address }, isAddressValid: complete, teamName: name });
    }

    handleTaxIdChange = ({ taxId, taxIdType, isTaxIdValid }: { taxId: string; taxIdType: Stripe.TaxId.Type | null; isTaxIdValid: boolean }) => {
        this.setState({ taxId, taxIdType, isTaxIdValid });
    }

    render() {
        const { t } = this.props;
        const {
            status,
            members,
            invite,
            teamName,
            isAddressValid,
            taxAmount,
            address,
            isTaxIdValid
        } = this.state;

        if (status === "loading") {
            return <Container><FetchingClickShield visible backgroundColor="white" /></Container>;
        }

        if (status === "wrongUser") {
            return (<ErrorDialog
                // @ts-ignore
                title={t("Wrong account")}
                message={t("You must have logged into a wrong account")}
                acceptCallback={() => window.location.href = `/logout?continue=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                acceptButtonText={t("Sign out")}
            />);
        }

        if (status === "inviteNotFound") {
            return (<ErrorDialog
                // @ts-ignore
                title={t("Wrong link")}
                message={t("There is something wrong with your link")}
                acceptCallback={() => window.location.href = "/"}
                acceptButtonText={t("Go to library")}
            />);
        }

        if (status === "alreadyAccepted") {
            return (<ErrorDialog
                // @ts-ignore
                title={t("Already accepted")}
                message={t("You've already accepted this invite")}
                acceptCallback={() => window.location.href = "/"}
                acceptButtonText={t("Go to library")}
            />);
        }

        if (status === "serverError") {
            return (<ErrorDialog
                // @ts-ignore
                title={t("Server error")}
                message={t("Something went terribly wrong, please contact us at support@beautiful.ai")}
                acceptCallback={() => window.location.href = "/"}
                acceptButtonText={t("Go to library")}
            />);
        }

        if (!invite) {
            return null;
        }

        if (status === "success") {
            return (<SuccessfulPurchaseDialog
                productDisplayName={t("Enterprise")}
                callToActionText={t("Continue")}
                message={t("You've signed up for the Beautiful.ai Enterprise Plan!")}
                trialDurationDays={invite.trialDurationDays}
                showTrial
                trialExpirationDate={moment().add(invite.trialDurationDays, "day").format("MMMM Do YYYY")}
                onCallToActionClick={() => browserHistory.push("/")}
            />);
        }

        const isFormValid = isAddressValid && isTaxIdValid &&
            !members.some(member => member.email.length > 0 && !emailRegex.test(member.email)) &&
            !members.some(member => members.some(otherMember => member.email && member.email.toLowerCase() === otherMember.email.toLowerCase() && member.id !== otherMember.id)) &&
            teamName;

        const invitedBy = invite.invitedByDisplayName ? `${invite.invitedByDisplayName} (${invite.invitedByEmail})` : invite.invitedByEmail;

        const taxString = taxAmount ? t(" plus ${{taxAmount}} tax", { taxAmount }) : "";
        const billingSummary = `$${invite.seatCount * invite.pricePerSeat}${taxString}/${invite.billingInterval}`;

        return (
            <Container>
                <ScrollContainer>
                    <ContentContainer style={{ maxWidth: 1200 }}>
                        <WorkspaceSettingsContainer>
                            <Heading>
                                <IconBox>
                                    <Icon blue>domain</Icon>
                                </IconBox>
                                {t("Create workspace")}
                            </Heading>
                            <InviteText>
                                <p>
                                    <Trans
                                        i18nKey="You were invited by <b>{{invitedBy}}</b> to start an enterprise trial."
                                        values={{ invitedBy }}
                                        components={{ b: <b /> }}
                                    />
                                </p>
                            </InviteText>
                            <AddTeamMembers
                                members={members}
                                onUpdateMember={this.handleMemberUpdate}
                                onAddMember={this.handleAddMember}
                                onRemoveMember={this.handleRemoveMember}
                                onImportMembersFromFile={this.handleImportMembersFromFile}
                                maxSeatCount={invite.seatCount}
                                availableRoles={Object.values(LegacyTeamMemberRole).filter(role => role !== LegacyTeamMemberRole.MEMBER)}
                            />
                        </WorkspaceSettingsContainer>
                        <PaymentDetailsContainer>
                            <AddressElement
                                options={{
                                    mode: "billing",
                                    defaultValues: {
                                        name: teamName,
                                        address: {
                                            country: this.guessedUserCountry
                                        }
                                    },
                                    display: {
                                        name: "organization"
                                    }
                                }}
                                onChange={this.handleAddressChange}
                            />
                            <TaxIdForm
                                countryCode={address.country}
                                initialTaxId={""}
                                onChange={this.handleTaxIdChange}
                                disabled={false}
                            />
                            <InviteInfoContainer>
                                <p>
                                    <Trans
                                        i18nKey="Your trial is <b>{{days}}</b> days and includes <b>{{seats}}</b> Pro Team seats."
                                        values={{ days: invite.trialDurationDays, seats: invite.seatCount }}
                                        components={{ b: <b /> }}
                                    />
                                    {" "}
                                    <Trans
                                        i18nKey="When the trial ends you'll be billed <price>{{billingSummary}}</price>."
                                        values={{ billingSummary }}
                                        components={{ price: <b /> }}
                                    />
                                </p>
                                <p>
                                    <Trans
                                        i18nKey="If you have questions about the enterprise plan, contact <mailto>{{email}}</mailto>"
                                        values={{ email: invite.invitedByEmail }}
                                        components={{ mailto: <a href={`mailto:${invite.invitedByEmail}`} /> }}
                                    />
                                </p>
                            </InviteInfoContainer>
                            <ButtonsContainer>
                                <Button
                                    blue
                                    large
                                    onClick={this.handleAcceptInvite}
                                    disabled={!isFormValid}
                                    data-test-id="accept-invite-button"
                                >
                                    {t("Continue")}
                                </Button>
                            </ButtonsContainer>
                        </PaymentDetailsContainer>
                    </ContentContainer>
                </ScrollContainer>
            </Container>
        );
    }
}

export default withStripe(withElements(withTranslation()(EnterpriseCheckout), { appearance }));
